import { useState } from "react";
import { useNotifyRequestsMutation } from "../hooks/useFeatureRequestsQueries";
import { errorMessage } from "../utils";
import DSDialog from "../../system-design/DSDialog";
import DSDialogTitle from "../../system-design/DSDialogTitle";
import DSDialogContent from "../../system-design/DSDialogContent";
import DSDialogActions from "../../system-design/DSDialogActions";
import DSTextField from "../../system-design/DSTextField";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import DSSecondaryButton from "../../system-design/DSSecondaryButton";
import DSAlert from "../../system-design/DSAlert";
import DSBox from "../../system-design/DSBox";
import DSTypography from "../../system-design/DSTypography";
import DSCircularProgress from "../../system-design/DSCircularProgress";

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

// Opens with a dry run so the admin sees exactly who will be emailed, per
// feature, before anything is sent. Sending marks the requests fulfilled.
const NotifyRequestsModal = (props: {
  open: boolean;
  requestIds: string[];
  onClose: () => void;
  onSent?: () => void;
}) => {
  const preview = useNotifyRequestsMutation();
  const send = useNotifyRequestsMutation();
  const [message, setMessage] = useState("");

  const onEnter = () => {
    setMessage("");
    send.reset();
    preview.mutate({ requestIds: props.requestIds, dryRun: true });
  };

  const groups = preview.data?.groups ?? [];
  const emailCount = groups.reduce((n, g) => n + g.recipients.length, 0);
  const sent = send.data && !send.data.dryRun ? send.data : null;

  return (
    <DSDialog
      open={props.open}
      onClose={props.onClose}
      fullWidth
      maxWidth="sm"
      TransitionProps={{ onEnter }}
    >
      <DSDialogTitle>Notify customers</DSDialogTitle>
      <DSDialogContent>
        <DSBox sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <DSTypography>
            Emails each customer that their requested feature is available, and
            marks the selected requests as fulfilled.
          </DSTypography>

          {preview.isPending ? (
            <DSBox sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <DSCircularProgress size={24} />
            </DSBox>
          ) : preview.isError ? (
            <DSAlert severity="error">
              {errorMessage(preview.error, "Failed to load recipients.")}
            </DSAlert>
          ) : sent ? (
            <DSAlert severity="success">
              Sent {plural(emailCount, "email")} and marked the requests as
              fulfilled.
            </DSAlert>
          ) : (
            <>
              {preview.data && preview.data.alreadyNotifiedCount > 0 && (
                <DSAlert severity="info">
                  {plural(preview.data.alreadyNotifiedCount, "request")} already
                  notified — skipped.
                </DSAlert>
              )}
              {groups.length === 0 ? (
                <DSAlert severity="info">Nobody left to notify.</DSAlert>
              ) : (
                <>
                  {groups.map((group) => (
                    <DSBox key={group.featureId}>
                      <DSTypography sx={{ fontWeight: 700, mb: 0.5 }}>
                        {group.featureTitle} ·{" "}
                        {plural(group.recipients.length, "recipient")}
                      </DSTypography>
                      <DSBox
                        sx={{
                          maxHeight: 120,
                          overflowY: "auto",
                          p: 1.5,
                          borderRadius: 1,
                          bgcolor: "action.hover",
                          fontSize: 13,
                          fontFamily: "monospace",
                        }}
                      >
                        {group.recipients.map((email) => (
                          <div key={email}>{email}</div>
                        ))}
                      </DSBox>
                    </DSBox>
                  ))}
                  <DSTextField
                    label="Message (optional)"
                    placeholder="Added to the email, e.g. how to get the update"
                    multiline
                    minRows={3}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                  />
                </>
              )}
            </>
          )}

          {send.isError && (
            <DSAlert severity="error">
              {errorMessage(send.error, "Failed to send the notifications.")}
            </DSAlert>
          )}
        </DSBox>
      </DSDialogContent>
      <DSDialogActions>
        <DSSecondaryButton onClick={props.onClose}>
          {sent ? "Close" : "Cancel"}
        </DSSecondaryButton>
        {!sent && (
          <DSPrimaryButton
            disabled={emailCount === 0 || preview.isPending || send.isPending}
            onClick={() =>
              send.mutate(
                {
                  requestIds: props.requestIds,
                  message: message.trim() || null,
                },
                { onSuccess: () => props.onSent?.() },
              )
            }
          >
            {send.isPending ? (
              <DSCircularProgress size={20} color="inherit" />
            ) : (
              `Send ${plural(emailCount, "email")}`
            )}
          </DSPrimaryButton>
        )}
      </DSDialogActions>
    </DSDialog>
  );
};

export default NotifyRequestsModal;
